import { action, observable, computed } from "mobx";
import { pick } from "@bonliva-crm/common";
import {
  ConsultantImport_RowFragment as Row,
  ConsultantImport_ImportConsultants as upload,
  ConsultantImport_Dedupe as dedupe,
  ConsultantImport_SaveConsultantSelection
} from "@graphql";
import { StateMachine } from "@modules/mobx-state-machine";
import { store } from "@store";
import { ConsultantImportResult, isInvalid, isOK } from "./utils";
import { FileUploadConfig } from "@bonliva-crm/shared";

type States = "init" | "editing" | "result";

export class ConsultantImportStore {
  fsm = new StateMachine<States>("init");
  uploadConfig = new FileUploadConfig("сonsultantImport");

  @observable isLoading = false;
  @observable file: File | null = null;
  @observable data: Row[] = [];
  @observable importResult: ConsultantImportResult | null = null;

  @observable isDeduping = false;
  @observable isDeduped = false;

  @observable withLookup = true;

  @computed get canSubmit() {
    return (
      this.validRows.length > 0 &&
      this.errors.length === 0 &&
      !this.isDeduping &&
      this.isDeduped
    );
  }

  @computed get errors() {
    return this.data.flatMap((x) => x.Errors);
  }

  @computed get isFileSelected() {
    return !!this.file;
  }

  @computed get validRows() {
    return this.data.filter((x) => x.Errors.length === 0 && !x.IsDuplicate);
  }

  constructor() {
    (window as any).consultantImportStore = this;
  }

  @action toEditing = (data: Row[]) => {
    this.data = data;
    this.isDeduped = false;
    this.withLookup = true;
    this.fsm.transition("editing");
  };

  @action toResult = (result: ConsultantImportResult) => {
    this.data = [];
    this.importResult = result;
    this.fsm.transition("result");
  };

  @action toInit = () => {
    this.file = null;
    this.data = [];
    this.fsm.transition("init");
  };

  @action startAgain = () => {
    this.toInit();
  };

  selectFile = () => {
    store()
      .upload.requestFileUpload({
        multiple: false,
        acceptMimeTypes: this.uploadConfig.acceptString
      })
      .then((files) => {
        files[0] && (this.file = files[0]);
      });
  };

  reselectFile = () => {
    store()
      .upload.requestFileUpload({
        multiple: false,
        acceptMimeTypes: this.uploadConfig.acceptString
      })
      .then((files) => {
        this.setFile(files[0]);
        this.processFile();
      });
  };

  setFile = (file: File) => {
    if (!file) {
      return;
    }

    this.file = file;
  };

  processFile = () => {
    if (!this.file) {
      return;
    }

    this.isLoading = true;

    upload
      .func({
        file: this.file,
        checkForDuplicates: false, // this.checkForDuplicates,
        bisnodeLookup: false // , this.bisnodeLookup
      })
      .then(({ data }) => {
        this.toEditing(data.importConsultants.Rows);
      })
      .catch((err) => {
        store().snackbars.error(`Cannot parse file: ${err.message}`);
        this.toInit();
      })
      .finally(() => {
        this.isLoading = false;
      });
  };

  @action saveSelected = async () => {
    this.isLoading = true;

    ConsultantImport_SaveConsultantSelection.func({
      rows: dataToInput(this.validRows),
      bisnodeLookup: this.withLookup
    })
      .then((data) => {
        const result: ConsultantImportResult = data.data.saveConsultantSelection as any;

        if (isOK(result)) {
          this.toResult(result);
        }

        if (isInvalid(result)) {
          store().snackbars.error("Something went wrong");
          this.toInit();
        }
      })
      .catch((err) => {
        store().snackbars.error(`Cannot import data\nReason: ${err.toString()}`);
      })
      .finally(() => {
        this.isLoading = false;
      });
  };

  dedupeRows = () => {
    this.isDeduping = true;

    dedupe
      .func({ rows: dataToInput(this.data) })
      .then(({ data }) => {
        this.data = data.consultantImportDeduplicate;
        this.isDeduped = true;
      })
      .catch((err) => {
        store().snackbars.error(`Cannot look items up\nReason: ${err}`);
      })
      .finally(() => {
        this.isDeduping = false;
      });
  };
}

function dataToInput(data: Row[]) {
  return data.map((x) => ({
    ...pick(x, ...pickFields),
    AreasOfExpertise: x.AreaOfExpertise
  }));
}

const pickFields: Array<keyof Row> = [
  "RowID",
  "FirstName",
  "LastName",
  "Phone",
  "CellPhone",
  "SocialSecurityNo",
  "Competitor",
  "ResponsiblePerson",
  "InitialComment",
  "HPRNumber",
  "isApprovedAuthorization",
  "country",
  "city",
  "streetName",
  "zipCode",
  "Meta"
];
